// Route-scoped static content for /travel-credit-card. All copy + links live
// here as consts (no runtime fetch) so the page can be fully SSR'd for SEO.
// Replace placeholder values with final copy/links from Figma (CBCC 2026).

// SEO copy adopted from the live CBCC page's tuned strings (see the CBCC SEO
// implementation guide). `cardImage` is the product image used in the
// FinancialProduct JSON-LD + as the OG/Twitter share image; `ogImage` mirrors
// it. `icon` is the page favicon. These feed both the route `head()` meta and
// the structured-data graph (structured-data.ts) — single source of truth.
export const SEO_CONTENT = {
	title: 'ixigo AU Bank Credit Card - Best Lifetime Free Travel Credit Card',
	description:
		'Apply online for ixigo AU Bank Travel Credit Card. Enjoy lifetime free membership, reward points, zero forex markup & travel benefits on flights, trains, hotels & buses.',
	canonicalUrl: 'https://www.ixigo.com/travel-credit-card',
	// product card image (also the OG/Twitter share image)
	cardImage:
		'https://images.ixigo.com/image/upload/payments/0fdc6536a75b0b2c63616b647b4eae94-ngsps.png',
	ogImage:
		'https://images.ixigo.com/image/upload/payments/0fdc6536a75b0b2c63616b647b4eae94-ngsps.png',
	icon: 'https://images.ixigo.com/image/upload/ixigo/6aaae6998a0dbccefca2ecd9f5a2f37b-mzybn.png',
} as const;

export const HERO_CONTENT = {
	logoAlt: 'ixigo',
	title: 'ixigo AU Credit Card',
	subtitle:
		'Your go-to travel card: Travel perks, fuel surcharge waiver,\nreward points and more on every spend',
	subtitleMweb:
		'Your go-to travel card: Travel perks, fuel surcharge waiver, reward points and more on every spend',
	cardImageAlt: 'ixigo AU Credit Card',
	networks: ['VISA', 'RuPay', 'AU Small Finance Bank'] as const,
	scrollCue: 'Keep Scrolling',
	cta: {
		// The apply CTA's label comes from the CVP response (homeScreen.ctaText);
		// this is the FALLBACK when CVP hasn't resolved one (logged-out, CVP fetch
		// failed, or the field is absent). See CbccCta.
		label: 'Apply Now',
	},
} as const;

// Deep-link targets into the long-form comparison page. Each "Learn More" on the
// home journey scrolls to the relevant section there via a URL hash; the comparison
// page reads the hash on load and scrolls to the matching element (see
// ComparisonPage's hash-scroll effect). The #ids MUST match section ids in
// comparison-content.ts (COMPARISON_CONTENT[].id) — verified at those sites.
//   first compare link (2nd benefit screen) → page top (the compare table)
//   carousel 1 (Reward Points)              → How Reward Points Work
//   carousel 2 (Concierge)                  → Real Spending Scenario
//   carousel 3 (Lounge)                     → Airport & Railway Lounge Access
//   carousel 4 (Insurance)                  → Real Spending Scenario
//   deal scene (flights, post-carousel)     → ixigo-Specific Co-Branded Benefits
const COMPARISON_PATH = '/travel-credit-card/comparison';
export const COMPARISON_LINKS = {
	top: COMPARISON_PATH,
	rewards: `${COMPARISON_PATH}#rewards-how-it-works`,
	realSpend: `${COMPARISON_PATH}#real-spend`,
	lounge: `${COMPARISON_PATH}#lounge-access`,
	coBranded: `${COMPARISON_PATH}#co-branded-benefits`,
	// the full FAQ accordion at the foot of the comparison page (#faqs anchor).
	faqs: `${COMPARISON_PATH}#faqs`,
} as const;

// Top-bar banners shown on the landing page for two CVP states (see CbccTopBar):
//   - rejected: status === 'REJECTED' → RED bar, NO dismiss, CTA hidden + page
//     locked to the first screen (no scroll).
//   - onboardingResume: userFlow === 'ONBOARDING' && showResumePopup === true →
//     YELLOW bar, DISMISSIBLE (cross); the full journey + CTA stay.
// Copy is static here (not backend-driven) per product.
export const CBCC_BANNERS = {
	rejected:
		'Sorry! Your application was not approved by the bank. You may re-apply for the card after 180 days.',
	onboardingResume: 'Ready to get your card? Complete your application now.',
} as const;

// The MWEB replacement for the onboarding-resume TOP BANNER: a card-style
// nudge floating above the fixed footer CTA (see CbccPendingNudge — web keeps
// the CbccTopBar bar for the same state). Copy is static per product.
export const CBCC_PENDING_NUDGE = {
	title: 'Application Pending',
	description:
		'Please complete your application process to get the ixigo AU credit card.',
} as const;

// The MWEB replacement for the rejected TOP BANNER (red strip): a mandatory
// bottom-sheet (see CbccRejectedSheet — web keeps the red CbccTopBar). The only
// action, Okay, sends the user to the ixigo homepage. Copy per design (longer
// than the strip's CBCC_BANNERS.rejected).
export const CBCC_REJECTED_SHEET = {
	iconUrl:
		'https://images.ixigo.com/image/upload/payments/610f3a5d4307cedf3b28e69fee48eb58-dqehg.webp',
	title: 'Card Application Not Approved',
	description:
		'Sorry! Your application was not approved by the bank based on their credit evaluation criteria. You may re-apply for the card after 180 days.',
	cta: 'Okay',
} as const;

// The pinned card scroll sequence shown right after the hero. Each step is one
// benefit panel revealed as the user scrolls while the card stays pinned.
// Copy inferred from the Figma frames — confirm/extend with final content.
export const CARD_SEQUENCE = {
	backgroundWord: 'LIFETIME FREE',
	// First "Learn More" of the journey → comparison page TOP (the compare table).
	compareLink: {
		label: 'Compare VISA and Rupay Cards',
		href: COMPARISON_LINKS.top,
	},
	scrollCue: 'Keep Scrolling',
	steps: [
		{
			id: 'forex',
			title: 'Zero Forex Markup',
			subtitle: '',
			// tagline blurs into focus AFTER the heading lands (see hero-stage PHASE 1)
			tagline: 'Spend Freely',
			description:
				'Ideal for international travel, the ixigo AU Credit Card comes with zero forex markup, helping you save on every spend abroad.',
			// mweb screen-3 copy — same \n contract as the fuel step below. The
			// subtext is a SHORT 2-line take on `description` (the full sentence
			// is 4+ lines at mweb size and would collide with the compare link);
			// placeholder until final mweb copy lands.
			titleMweb: 'Zero Forex Markup,\nSpend Freely',
			descriptionMweb: 'Save on every spend abroad\nwith zero forex markup.',
		},
		{
			id: 'fuel',
			title: '1% Fuel Surcharge Waiver,',
			subtitle: '',
			// tagline blurs into focus AFTER the heading lands (see hero-stage PHASE 2)
			tagline: 'Save More',
			description: '1% fuel surcharge waiver of up to ₹250 per month.',
			// mweb screen-2 copy (web uses title/tagline/description). The \n are
			// REAL line breaks (rendered via whitespace-pre-line): each block
			// reveals through a fixed-height mask, so the wrap points must be
			// deterministic — never left to the browser.
			titleMweb: '1% Fuel Surcharge\nWaiver, Save More',
			descriptionMweb: 'Enjoy extra savings every time\nyou refuel.',
		},
	],
} as const;

// Caption shown over the bottom of each enlarged showcase image — one per slide,
// in the same order as ASSETS.gallery (POS, concierge, lounge, insurance). Each
// caption's "Learn More" deep-links into the comparison page (`href`); the label
// is shared (SHOWCASE_LEARN_MORE.label).
export const SHOWCASE_CAPTIONS = [
	{
		value: '2.5',
		unit: '%',
		title: 'Reward Points',
		description: 'Earn rewards on EVERY spend.',
		href: COMPARISON_LINKS.rewards,
	},
	{
		value: '24/7',
		unit: '',
		title: 'Concierge Services',
		description:
			'Available on the RuPay variant for assistance with travel bookings, reservations, etc.',
		href: COMPARISON_LINKS.realSpend,
	},
	{
		value: '16',
		unit: '',
		title: 'Complimentary Lounge Visits',
		description:
			'Enjoy lounge access across airports and railway stations throughout the year.',
		href: COMPARISON_LINKS.lounge,
	},
	{
		value: '₹2 Lakh',
		unit: '',
		title: 'Trip Protection*',
		description: 'Travel with added peace of mind on every trip.',
		href: COMPARISON_LINKS.realSpend,
	},
] as const;

// Persistent section heading shown (top-left) across the whole showcase carousel.
// Two lines (already rendered uppercase) — one <span> per line.
export const SHOWCASE_HEADING = ['No Catch.', 'Just Rewards.'] as const;

// Shared "Learn More" LABEL for the showcase captions + the deal scene. The
// per-caption destinations live on each SHOWCASE_CAPTIONS[].href (different
// comparison sections); the deal scene uses COMPARISON_LINKS.coBranded. `href`
// here is the deal-scene fallback / generic target (comparison co-branded
// section) — kept so existing consumers still resolve a destination.
export const SHOWCASE_LEARN_MORE = {
	label: 'LEARN MORE',
	href: COMPARISON_LINKS.coBranded,
} as const;

// Full-screen flight-window "deal" scene — the final beat. Copy fades in once
// the plane window has expanded to fill the screen.
export const DEALS_SCENE = {
	badge: {
		value: '10%',
		label: 'Off on Flight\nBookings',
		// mweb variant (per the mweb design comp) — singular "Booking".
		labelMweb: 'Off on Flight\nBooking',
	},
	heading: 'Exclusive Travel Deals\nFor Every Journey',
	description: 'Access curated offers across flights, stays, buses and trains.',
	// mweb variants (per the mweb design comp); \n follows the copy convention.
	headingMweb: 'Exclusive Travel Deals,\nEverywhere You Go',
	descriptionMweb:
		'Access curated deals on hotels, flights, buses, and trains—premium travel without the premium price.',
	nav: ['Flights', 'Hotels', 'Buses', 'Trains'],
} as const;

// Hotel destination scene (second nav stop) — valley framed in the room window.
export const HOTEL_SCENE = {
	badge: { value: '10%', label: 'Off on Hotel\nBookings' },
} as const;

// Bus destination scene — interior bus window onto the mountains.
export const BUS_SCENE = {
	badge: { value: '10%', label: 'Off on Bus\nBookings' },
} as const;

// Train destination scene (final clip) — interior train window onto the valley.
export const TRAIN_SCENE = {
	badge: { value: '₹0', label: 'Payment Gateway Fee\non Train Bookings' },
} as const;
// /travel-credit-card/apply — copy for the dark application page. Replace
// placeholder values with final Figma copy/links.
export const APPLY_CONTENT = {
	backToHome: 'Back to home',
	form: {
		title: 'Apply for ixigo AU Credit Card',
		subtitle: 'Enter your pincode to check availability in your area.',
		// mweb form: same title/subtitle as web; only the pincode label differs.
		pincodeLabelMweb: 'Enter Pincode',
		pincodeLabel: 'Pincode',
		pincodeChecking: 'Checking availability…',
		pincodeInvalid: 'Enter a valid 6-digit pincode.',
		pincodeSuccess: 'Yay! The card can be delivered at your pin code.',
		pincodeFailure: (pincode: string) => `We don't deliver to ${pincode} yet`,
		// shown (in a collapsible alert) below the pincode when delivery is
		// NOT available for the checked pincode.
		deliveryHint: 'Try your home/work pincode — delivery may be available',
		fields: {
			name: 'Full name',
			phone: 'Mobile number',
			email: 'Email',
			dob: 'DOB (DD/MM/YYYY)',
			gender: 'Gender',
			employmentType: 'Employment type',
		},
		genders: [
			{ value: 'MALE', label: 'Male' },
			{ value: 'FEMALE', label: 'Female' },
			{ value: 'OTHER', label: 'Other' },
		],
		submitLabel: 'Apply Now',
		agreementPrefix: 'By submitting, you agree to the',
		// TODO: real T&C + privacy URLs from legal/CBCC docs.
		tnc: {
			href: 'https://www.aubank.in/credit-card-member-agreement-pdf-credit-card-member-agreement.pdf',
			label: 'Terms & Conditions',
		},
		privacy: {
			href: 'https://www.aubank.in/SOC_CC-MITC(with-Schedule-of-Charges).pdf',
			label: 'Privacy Policy',
		},
	},
	// Testimonial carousel (left panel on lg+). Replace with final copy/avatars.
	testimonials: [
		{
			name: 'Rahul Sharma',
			quote:
				'I use my one monthly flight discount on my Delhi–Mumbai work trip and save upto ₹3000 every single time. ixigo finds the best fares and the card seals the deal — this combo is unbeatable!',
			avatar: '',
			city: 'Delhi',
		},
		{
			name: 'Priya Venkataraman',
			quote:
				'No payment gateway charges on any IRCTC booking via ixigo. For someone who books trains frequently, this card is the best!',
			avatar: '',
			city: 'Bengaluru',
		},
		{
			name: 'Arjun Mehta',
			quote:
				"I save my monthly 10% discount for my Pune–Goa Volvo AC sleeper trip and get discount of upto ₹300 effortlessly. ixigo's bus search is top-notch and this card makes every ride cheaper!",
			avatar: '',
			city: 'Pune',
		},
		{
			name: 'Neha Gupta',
			quote:
				"I plan my monthly hotel stay around this discount — saved ₹3000 on a Jaipur property last month. ixigo's hotel listings are accurate and the ixigo AU card discount makes every stay feel like an upgrade!",
			avatar: '',
			city: 'Mumbai',
		},
		{
			name: 'Ashwin',
			quote:
				"Swiped across Singapore and Dubai with zero markup fees — saved over ₹4,000 in forex charges on one trip alone. ixigo got me the cheapest flight as well, so it's a complete travel card!",
			avatar: '',
			city: 'Chennai',
		},
	],
} as const;
